import { useState, useEffect } from "react";
import { signaturesApi } from "@services/api/signatures";
import { organizationsApi } from "@services/api/organizations";
import type { SignatureToken } from "types/signatureToken";
import { navigateTo } from "context/navigation.service";
import Icon1 from "@assets/icons/Icon";
import { SignaturesChart, SignatureSuccessRateChart } from "@components/charts/SignaturesChart";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: "En attente", color: "#D97706", bg: "#FEF3C7" },
  signed: { label: "Signé", color: "#059669", bg: "#D1FAE5" },
  superseded: { label: "Remplacé", color: "#6B7280", bg: "#F3F4F6" },
  expired: { label: "Expiré", color: "#DC2626", bg: "#FEE2E2" },
};

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  compte_rendu: { label: "Compte rendu", color: "#7C3AED", bg: "#EDE9FE" },
  ordre_devis: { label: "Devis", color: "#6366F1", bg: "#EEF2FF" },
};

type Tab = "config" | "history";

export function Signatures() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const [activeTab, setActiveTab] = useState<Tab>("config");
  const [tokens, setTokens] = useState<SignatureToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<"all" | "pending" | "signed">("all");

  // Config state
  const currentLevel = orgData?.signature_config?.signature_level || "simple";
  const [selectedLevel, setSelectedLevel] = useState(currentLevel);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedLevel(currentLevel);
  }, [currentLevel]);

  useEffect(() => {
    setLoading(true);
    signaturesApi
      .listTokens()
      .then(setTokens)
      .catch((e) => console.error("Failed to load signature tokens:", e))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveLevel = async () => {
    if (!orgData || selectedLevel === currentLevel) return;
    setSaving(true);
    try {
      await organizationsApi.update(orgData.id, {
        signature_config: { signature_level: selectedLevel },
      });
      fetchOrgData(orgData.id);
      showNotification({ message: "Niveau de signature mis à jour", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const filtered = filter === "all"
    ? tokens
    : tokens.filter((t) => t.status === filter);

  const counts = {
    all: tokens.length,
    pending: tokens.filter((t) => t.status === "pending").length,
    signed: tokens.filter((t) => t.status === "signed").length,
  };

  const formatDate = (iso: string) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const getContextLine = (t: SignatureToken): string => {
    const parts: string[] = [];
    if (t.ordre_num) parts.push(`OR #${t.ordre_num}`);
    if (t.ordre_name) parts.push(t.ordre_name);
    if (t.project_name) parts.push(t.project_name);
    if (t.document_name) parts.push(t.document_name);
    return parts.join(" — ");
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "config", label: "Configuration" },
    { key: "history", label: "Historique" },
  ];

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1
        className="text-hx font-semibold mb-2"
        style={{ fontFamily: "Poppins" }}
      >
        Signatures électroniques
      </h1>
      <p
        className="text-h2 text-neutral-500 mb-6"
        style={{ fontFamily: "Poppins" }}
      >
        Configurez le niveau de signature, suivez vos demandes et votre consommation.
      </p>

      {/* Tabs */}
      <div className="flex flex-row gap-1 mb-6" style={{ borderBottom: "2px solid #E5E7EB" }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-2"
            style={{
              fontFamily: "Poppins",
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? "#111827" : "#6B7280",
              borderBottom: activeTab === tab.key ? "2px solid #111827" : "none",
              marginBottom: "-2px",
              background: "transparent",
              border: "none",
              borderBottomWidth: activeTab === tab.key ? 2 : 0,
              borderBottomStyle: "solid",
              borderBottomColor: activeTab === tab.key ? "#111827" : "transparent",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* CONFIG TAB */}
      {activeTab === "config" && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h2 className="text-h1 font-semibold" style={{ fontFamily: "Poppins" }}>
              Niveau de signature
            </h2>
            <p className="text-h3 text-neutral-500" style={{ fontFamily: "Poppins" }}>
              Choisissez le niveau de sécurité pour les signatures de vos documents.
            </p>

            {/* Simple tier */}
            <button
              onClick={() => setSelectedLevel("simple")}
              className="flex flex-col p-5 rounded-xl text-left cursor-pointer transition-all"
              style={{
                background: selectedLevel === "simple" ? "#F0FDF4" : "#FAFAFA",
                border: `2px solid ${selectedLevel === "simple" ? "#059669" : "#E5E7EB"}`,
              }}
            >
              <div className="flex flex-row items-center justify-between mb-2">
                <span className="text-h1 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
                  Signature simple
                </span>
                <span className="text-h2 font-bold" style={{ fontFamily: "Poppins", color: "#059669" }}>
                  0,05 {"\u20AC"} / signature
                </span>
              </div>
              <span className="text-h3" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                Signature électronique de base avec piste d'audit. Le signataire dessine ou télécharge
                sa signature via un lien sécurisé. Un certificat de preuve est généré automatiquement.
              </span>
              <div className="flex flex-row gap-2 mt-3 flex-wrap">
                {["Lien sécurisé (UUID)", "Empreinte SHA-256", "Adresse IP enregistrée", "Certificat de preuve PDF"].map((f) => (
                  <span key={f} className="text-h5 px-2 py-0.5 rounded-full" style={{ fontFamily: "Poppins", background: "#D1FAE5", color: "#065F46" }}>
                    {f}
                  </span>
                ))}
              </div>
            </button>

            {/* Advanced tier */}
            <button
              onClick={() => setSelectedLevel("advanced")}
              className="flex flex-col p-5 rounded-xl text-left cursor-pointer transition-all"
              style={{
                background: selectedLevel === "advanced" ? "#EFF6FF" : "#FAFAFA",
                border: `2px solid ${selectedLevel === "advanced" ? "#2563EB" : "#E5E7EB"}`,
              }}
            >
              <div className="flex flex-row items-center justify-between mb-2">
                <span className="text-h1 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
                  Signature avancée
                </span>
                <span className="text-h2 font-bold" style={{ fontFamily: "Poppins", color: "#2563EB" }}>
                  0,20 {"\u20AC"} / signature
                </span>
              </div>
              <span className="text-h3" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                Signature renforcée avec vérification d'identité par SMS (OTP), signature numérique
                PAdES intégrée au PDF, et horodatage certifié RFC 3161. Conforme eIDAS niveau avancé.
              </span>
              <div className="flex flex-row gap-2 mt-3 flex-wrap">
                {[
                  "Tout de la signature simple",
                  "Vérification SMS (OTP)",
                  "Signature PAdES dans le PDF",
                  "Horodatage RFC 3161",
                ].map((f) => (
                  <span key={f} className="text-h5 px-2 py-0.5 rounded-full" style={{ fontFamily: "Poppins", background: "#DBEAFE", color: "#1E40AF" }}>
                    {f}
                  </span>
                ))}
              </div>
            </button>

            {selectedLevel !== currentLevel && (
              <button
                onClick={handleSaveLevel}
                disabled={saving}
                className="self-end px-6 py-2 rounded-lg text-white font-semibold transition-all"
                style={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  background: saving ? "#94A3B8" : "#111827",
                  cursor: saving ? "not-allowed" : "pointer",
                  border: "none",
                }}
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            )}
          </div>

          {/* Pricing info */}
          <div
            className="p-4 rounded-xl"
            style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
          >
            <span className="text-h3" style={{ fontFamily: "Poppins", color: "#92400E" }}>
              Les signatures sont débitées du solde communications (partagé avec WhatsApp, emails et IA).
              Simple : 0,05 {"\u20AC"} (certificat de preuve + empreinte SHA-256). Avancée : 0,20 {"\u20AC"} (SMS OTP + PAdES + horodatage).
            </span>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === "history" && (
        <div className="flex flex-col gap-6">
          {/* Charts */}
          {tokens.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
                <SignaturesChart tokens={tokens} showPeriodSelector defaultPeriod="12m" />
              </div>
              <div className="p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
                <SignatureSuccessRateChart tokens={tokens} />
              </div>
            </div>
          )}

          {/* Stats cards */}
          <div className="flex flex-row gap-3">
            {([
              { key: "all" as const, label: "Total", value: counts.all, color: "#111827", bg: "#F3F4F6" },
              { key: "pending" as const, label: "En attente", value: counts.pending, color: "#D97706", bg: "#FEF3C7" },
              { key: "signed" as const, label: "Signées", value: counts.signed, color: "#059669", bg: "#D1FAE5" },
            ]).map((stat) => (
              <button
                key={stat.key}
                onClick={() => setFilter(stat.key)}
                className="flex flex-col items-center flex-1 p-4 rounded-xl cursor-pointer transition-all"
                style={{
                  background: filter === stat.key ? stat.bg : "#FAFAFA",
                  border: `1.5px solid ${filter === stat.key ? stat.color + "40" : "#E5E7EB"}`,
                }}
              >
                <span
                  className="text-hx font-bold"
                  style={{ fontFamily: "Poppins", color: stat.color }}
                >
                  {stat.value}
                </span>
                <span
                  className="text-h4"
                  style={{ fontFamily: "Poppins", color: "#6B7280" }}
                >
                  {stat.label}
                </span>
              </button>
            ))}
          </div>

          {/* List */}
          <div>
            <h2
              className="text-h1 font-semibold mb-4"
              style={{ fontFamily: "Poppins" }}
            >
              Historique des signatures
            </h2>
            {loading ? (
              <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                Chargement...
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-h3 text-neutral-400" style={{ fontFamily: "Poppins" }}>
                {filter === "all"
                  ? "Aucune demande de signature pour le moment."
                  : `Aucune signature ${filter === "pending" ? "en attente" : "signée"}.`}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {filtered.map((t) => {
                  const s = STATUS_LABELS[t.status] || STATUS_LABELS.pending;
                  const tp = TYPE_LABELS[t.type] || TYPE_LABELS.compte_rendu;
                  const context = getContextLine(t);
                  return (
                    <div
                      key={t.id}
                      className="flex flex-row items-center justify-between p-4 rounded-xl"
                      style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
                    >
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <div className="flex flex-row items-center gap-2 flex-wrap">
                          <span
                            className="text-h4 font-medium px-2 py-0.5 rounded-full"
                            style={{ fontFamily: "Poppins", color: s.color, background: s.bg }}
                          >
                            {s.label}
                          </span>
                          <span
                            className="text-h4 font-medium px-2 py-0.5 rounded-full"
                            style={{ fontFamily: "Poppins", color: tp.color, background: tp.bg }}
                          >
                            {tp.label}
                          </span>
                          {context && (
                            <span
                              className="text-h4 font-medium truncate"
                              style={{ fontFamily: "Poppins", color: "#374151" }}
                            >
                              {context}
                            </span>
                          )}
                        </div>

                        {(t.signer_name || t.signer_email) && (
                          <span
                            className="text-h3 mt-0.5"
                            style={{ fontFamily: "Poppins", color: "#111827" }}
                          >
                            {t.signer_name}
                            {t.signer_email && (
                              <span style={{ color: "#6B7280" }}>
                                {t.signer_name ? ` — ${t.signer_email}` : t.signer_email}
                              </span>
                            )}
                          </span>
                        )}

                        {t.conductor_name && (
                          <span
                            className="text-h5"
                            style={{ fontFamily: "Poppins", color: "#9CA3AF" }}
                          >
                            Envoyé par {t.conductor_name}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col items-end shrink-0 ml-4 gap-1">
                        <span
                          className="text-h5"
                          style={{ fontFamily: "Poppins", color: "#9CA3AF" }}
                        >
                          Envoyé {formatDate(t.created_at)}
                        </span>
                        {t.signed_at && (
                          <span
                            className="text-h5"
                            style={{ fontFamily: "Poppins", color: "#059669" }}
                          >
                            Signé {formatDate(t.signed_at)}
                          </span>
                        )}
                        <div className="flex flex-row gap-1 mt-1">
                          {t.ordre_id && (
                            <button
                              onClick={() => navigateTo({ route: `/ordre/${t.ordre_id}` })}
                              className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-neutral-200 transition-colors"
                              style={{ background: "#F3F4F6" }}
                              title="Voir l'ordre"
                            >
                              <Icon1 name="open" size={12} color="#6B7280" />
                              <span className="text-h5" style={{ fontFamily: "Poppins", color: "#6B7280" }}>Ordre</span>
                            </button>
                          )}
                          {t.pdf_url && (
                            <button
                              onClick={() => window.open(t.pdf_url, "_blank")}
                              className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-neutral-200 transition-colors"
                              style={{ background: "#F3F4F6" }}
                              title="Voir le PDF"
                            >
                              <Icon1 name="document" size={12} color="#6B7280" />
                              <span className="text-h5" style={{ fontFamily: "Poppins", color: "#6B7280" }}>PDF</span>
                            </button>
                          )}
                          {t.status === "signed" && t.audit_trail_url && (
                            <button
                              onClick={() => window.open(t.audit_trail_url, "_blank")}
                              className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-neutral-200 transition-colors"
                              style={{ background: "#EDE9FE" }}
                              title="Certificat de preuve"
                            >
                              <Icon1 name="document" size={12} color="#7C3AED" />
                              <span className="text-h5" style={{ fontFamily: "Poppins", color: "#7C3AED" }}>Preuve</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
